import React, { useState, useEffect } from 'react';
import { clubDocumentApi } from '../../../../services/clubDocumentApi';
import Badge from '../../../Badge';
import ErrorBanner from '../../../common/ErrorBanner';

/**
 * Step 5: Review and Create/Update
 *
 * Purpose: Review all template settings and submit for creation or update
 *
 * Features:
 * - Display all configured settings
 * - Preview template content
 * - Show test PDF info if generated
 * - Submit template creation or update
 * - Handle errors and success
 */
const Step5_ReviewAndCreate = ({
  wizardData,
  onDataChange,
  onSuccess,
  onError,
  setIsLoading,
  onValidationChange,
  triggerSubmit, // New prop to trigger submission from wizard
  isEditMode = false // Whether we're editing an existing template
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // This step is always valid for navigation
  useEffect(() => {
    onValidationChange(true);
  }, [onValidationChange]);

  // Handle external submit trigger from wizard finish button
  useEffect(() => {
    if (triggerSubmit && !isSubmitting && !hasSubmitted) {
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerSubmit]);

  const handleSubmit = async () => {
    const actionLabel = isEditMode ? 'update' : 'create';
    try {
      setIsSubmitting(true);
      setIsLoading(true);
      setErrors([]);

      // Validate one more time
      if (!wizardData.title?.trim()) {
        setErrors(['Title is required']);
        setIsSubmitting(false);
        setIsLoading(false);
        return;
      }

      if (!wizardData.content?.trim()) {
        setErrors(['Content is required']);
        setIsSubmitting(false);
        setIsLoading(false);
        return;
      }

      // Prepare submission data
      const submitData = {
        title: wizardData.title,
        description: wizardData.description || '',
        content: wizardData.content,
        elements_json: wizardData.elements_json || '',
        category: wizardData.category,
        acceptance_mode: wizardData.acceptance_mode,
        active: wizardData.active
      };

      console.log(`[Step5] ${isEditMode ? 'Updating' : 'Creating'} template:`, submitData);

      // Create or update template based on mode
      let response;
      if (isEditMode && wizardData.templateId) {
        response = await clubDocumentApi.updateTemplate(wizardData.templateId, submitData);
      } else {
        response = await clubDocumentApi.createTemplate(submitData);
      }

      if (response.success || response.data?.success) {
        setHasSubmitted(true);
        onSuccess(response);
      } else {
        setErrors([`Failed to ${actionLabel} template. Please try again.`]);
        setIsSubmitting(false);
        setIsLoading(false);
        onError(new Error(`Failed to ${actionLabel} template`));
      }
    } catch (err) {
      console.error(`Error ${actionLabel}ing template:`, err);
      const errorMessage = err.response?.data?.detail || `Failed to ${actionLabel} template. Please check your connection and try again.`;
      setErrors([errorMessage]);
      setIsSubmitting(false);
      setIsLoading(false);
      onError(err);
    }
  };

  const getCategoryLabel = (category) => {
    const categories = {
      waiver: 'Waiver',
      medical: 'Medical',
      registration: 'Registration',
      emergency_contact: 'Emergency Contact',
      concussion: 'Concussion',
      insurance: 'Insurance',
      public_profile_display: 'Public Information Sharing Agreement',
      other: 'Other'
    };
    return categories[category] || category;
  };

  const getAcceptanceModeLabel = (mode) => {
    const modes = {
      accept: 'Digital Signature',
      acknowledge: 'Simple Acknowledgment'
    };
    return modes[mode] || mode;
  };

  if (hasSubmitted) {
    return (
      <div className="space-y-6 text-center py-8">
        <div className="text-green-600 text-6xl">✓</div>
        <h3 className="text-2xl font-semibold text-gray-900">
          Template {isEditMode ? 'Updated' : 'Created'} Successfully!
        </h3>
        <p className="text-gray-600">
          Your template "{wizardData.title}" has been {isEditMode ? 'updated' : 'created'} and is now available for use.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Review Template Configuration</h3>
        <p className="text-sm text-gray-600 mb-6">
          Please review your template settings before {isEditMode ? 'updating' : 'creating'}. You can go back to make changes if needed.
        </p>
      </div>

      {/* Error Banner */}
      {errors.length > 0 && (
        <ErrorBanner
          type="error"
          messages={errors}
          onDismiss={() => setErrors([])}
        />
      )}

      {/* Template Information */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
        <div>
          <h4 className="text-sm font-semibold text-gray-500 mb-1">Template Title</h4>
          <p className="text-lg font-semibold text-gray-900">{wizardData.title}</p>
        </div>

        {wizardData.description && (
          <div>
            <h4 className="text-sm font-semibold text-gray-500 mb-1">Description</h4>
            <p className="text-gray-700">{wizardData.description}</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-500 mb-1">Category</h4>
            <Badge variant="info">{getCategoryLabel(wizardData.category)}</Badge>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-500 mb-1">Acceptance Mode</h4>
            <Badge variant="info">{getAcceptanceModeLabel(wizardData.acceptance_mode)}</Badge>
          </div>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-gray-500 mb-1">Status</h4>
          <Badge variant={wizardData.active ? 'success' : 'warning'}>
            {wizardData.active ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      </div>

      {/* Test PDF Info */}
      {wizardData.testPdfUrl && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-green-800">
            <span className="text-lg">✓</span>
            <div>
              <h4 className="font-semibold">Test PDF Generated</h4>
              <p className="text-sm">A test PDF was successfully generated and reviewed in the previous step.</p>
            </div>
          </div>
        </div>
      )}

      {/* Content Preview */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-sm font-semibold text-gray-500 mb-3">Content Preview</h4>
        <div
          className="prose max-w-none p-4 bg-gray-50 rounded border border-gray-200 max-h-96 overflow-y-auto"
          dangerouslySetInnerHTML={{ __html: wizardData.content }}
        />
      </div>

      {/* Content Stats */}
      <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-semibold">Content Length:</span> {wizardData.content?.length || 0} characters
          </div>
          <div>
            <span className="font-semibold">Form Elements:</span> {wizardData.elements?.length || 0} elements
          </div>
        </div>
      </div>

      {/* Info: Button moved to wizard footer */}
      <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
        <p className="text-sm text-center">
          Click "{isEditMode ? 'Update Template' : 'Create Template'}" below to finalize and {isEditMode ? 'update' : 'create'} your template.
        </p>
      </div>
    </div>
  );
};

export { Step5_ReviewAndCreate };
